import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Step,
  StepLabel,
  Stepper,
  Stack,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import CodeIcon from "@mui/icons-material/Code";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { IconTile, PageHeader } from "../components/ui";
import { api } from "../api/axios";

export default function SiteOnboarding() {
  const { id } = useParams();
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  const BackIcon = isRtl ? ArrowForwardRoundedIcon : ArrowBackRoundedIcon;
  const NextIcon = isRtl ? ArrowBackRoundedIcon : ArrowForwardRoundedIcon;
  const [site, setSite] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState("");
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const steps = useMemo(
    () => [
      { key: "domain", label: "דומיין והגדרות" },
      { key: "install", label: "הטמעה באתר" },
      { key: "verify", label: "בדיקת טעינה" },
      { key: "statement", label: "הצהרת נגישות" },
      { key: "compliance", label: "Compliance" },
    ],
    []
  );

  useEffect(() => {
    setError("");
    api
      .get("/api/sites")
      .then(({ data }) => {
        const s = (data.sites || []).find((x) => String(x.id) === String(id));
        if (!s) throw new Error("not_found");
        setSite(s);
      })
      .catch((err) => setError(err?.response?.data?.message || err?.message || "שגיאה"));
  }, [id]);

  const runVerify = async () => {
    setError("");
    setVerify(null);
    setVerifying(true);
    try {
      const { data } = await api.get(`/api/sites/${id}/verify-embed`);
      setVerify(data);
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה בבדיקה");
    } finally {
      setVerifying(false);
    }
  };

  const next = () => setActiveStep((s) => Math.min(steps.length - 1, s + 1));
  const back = () => setActiveStep((s) => Math.max(0, s - 1));

  const stepMeta = [
    { icon: <LanguageOutlinedIcon />, tone: "primary" },
    { icon: <CodeIcon />, tone: "violet" },
    { icon: <TravelExploreOutlinedIcon />, tone: "teal" },
    { icon: <DescriptionOutlinedIcon />, tone: "warning" },
    { icon: <VerifiedOutlinedIcon />, tone: "success" },
  ];

  const stepBody = (title, text, children) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems="flex-start">
      <IconTile size={52} tone={stepMeta[activeStep].tone}>
        {stepMeta[activeStep].icon}
      </IconTile>
      <Box sx={{ flex: 1, minWidth: 0, display: "grid", gap: 1.5 }}>
        <Box>
          <Typography variant="overline" component="div">
            שלב {activeStep + 1} מתוך {steps.length}
          </Typography>
          <Typography variant="h5" component="h2">
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75, maxWidth: 640 }}>
            {text}
          </Typography>
        </Box>
        {children}
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Button component={Link} to="/sites" size="small" startIcon={<BackIcon />} sx={{ mb: 1.5, color: "text.secondary", minHeight: 30, px: 1 }}>
        חזרה לרשימת אתרים
      </Button>
      <PageHeader
        eyebrow="התחלה מהירה"
        title={site ? site.name : "התחלה מהירה"}
        subtitle={site ? <>דומיין: {site.domain}</> : "טוען..."}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Card>
        <Box sx={{ px: { xs: 1, sm: 3 }, pt: 3, pb: 2.5, borderBottom: 1, borderColor: "divider", bgcolor: (t) => t.aw.softer, overflowX: "auto" }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ minWidth: 520 }}>
            {steps.map((s, i) => (
              <Step key={s.key} completed={i < activeStep}>
                <StepLabel sx={{ "& .MuiStepLabel-label": { fontWeight: 600, fontSize: "0.82rem" } }}>{s.label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 4 }, minHeight: 220 }}>
          {site ? (
            <>
              {activeStep === 0
                ? stepBody(
                    "בדוק שהדומיין שלך נכון",
                    "הווידג׳ט נטען רק מדומיינים שאישרת. אפשר כמה דומיינים מופרדים בפסיק, כולל `www`.",
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component={Link} to={`/sites/${id}`} variant="contained">
                        עריכת דומיין/מיתוג
                      </Button>
                      <Button component={Link} to={`/sites/${id}/install`} variant="outlined">
                        מעבר למסך הטמעה
                      </Button>
                    </Stack>
                  )
                : null}

              {activeStep === 1
                ? stepBody(
                    "הטמע את הסקריפט באתר",
                    <>במסך “הטמעה” תקבל snippet מוכן. הטמע אותו לפני {"</body>"} או דרך מנהל התגיות.</>,
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component={Link} to={`/sites/${id}/install`} variant="contained">
                        פתח מסך הטמעה
                      </Button>
                      <Button component={Link} to={`/sites/${id}`} variant="outlined">
                        עריכת אתר
                      </Button>
                    </Stack>
                  )
                : null}

              {activeStep === 2
                ? stepBody(
                    "בדוק שה־snippet באמת נמצא בדף",
                    "אנחנו נסרוק את ה־HTML של הדומיין ונחפש `siteKey` + `widget/embed.js`. אם האתר חסום (BasicAuth/robots), הבדיקה לא תצליח—ואז נבדוק ידנית דרך View Source.",
                    <>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                        <Button
                          onClick={runVerify}
                          variant="contained"
                          disabled={verifying}
                          startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : null}
                        >
                          {verifying ? "בודק..." : "בדוק הטמעה"}
                        </Button>
                      </Stack>

                      {verify ? (
                        <Alert severity={verify.ok ? "success" : "warning"}>
                          {verify.ok ? (
                            <>נראה שהווידג׳ט הוטמע בהצלחה בדומיין ({verify.best?.url}).</>
                          ) : (
                            <>
                              לא מצאנו עדיין הטמעה מלאה. בדקנו: <b>{verify.best?.url}</b> (סטטוס: {verify.best?.status})
                            </>
                          )}
                        </Alert>
                      ) : null}

                      {verify?.best?.blockingSnippet ? (
                        <Alert severity="warning">
                          באתר מוטמע קוד ההטמעה הישן שחוסם את טעינת העמוד אם שירות הנגישות לא זמין. החלף אותו בקוד החדש
                          ממסך ״הטמעה״ (או עדכן את תוסף הוורדפרס לגרסה 1.1.0).
                        </Alert>
                      ) : null}

                      {verify && !verify.ok && Array.isArray(verify.tips) && verify.tips.length ? (
                        <Box sx={{ p: 2, borderRadius: 1.5, bgcolor: (t) => t.aw.soft }}>
                          <Typography variant="subtitle2" component="h3" sx={{ color: "text.primary", mb: 0.5 }}>
                            טיפים:
                          </Typography>
                          <Box component="ul" sx={{ m: 0, paddingInlineStart: 2.5 }}>
                            {verify.tips.map((t, idx) => (
                              <Typography component="li" key={idx} variant="body2" sx={{ color: "text.secondary" }}>
                                {t}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                      ) : null}
                    </>
                  )
                : null}

              {activeStep === 3
                ? stepBody(
                    "צור/פרסם הצהרת נגישות",
                    "ההצהרה תוצג בעמוד הציבורי ותהיה נגישה גם מהווידג׳ט.",
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component={Link} to={`/sites/${id}/statement`} variant="contained">
                        פתח הצהרת נגישות
                      </Button>
                      <Button component={Link} to={`/sites/${id}/install`} variant="outlined">
                        חזור להטמעה
                      </Button>
                    </Stack>
                  )
                : null}

              {activeStep === 4
                ? stepBody(
                    "בדיקת Compliance ודוח",
                    "כאן תראה ציון תאימות, Checklist ודוח להדפסה.",
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button component={Link} to={`/sites/${id}/compliance`} variant="contained">
                        פתח Compliance
                      </Button>
                      <Button component={Link} to={`/sites/${id}/compliance-report`} variant="outlined">
                        דוח להדפסה
                      </Button>
                    </Stack>
                  )
                : null}
            </>
          ) : (
            <Stack spacing={1.5}>
              <Skeleton variant="rounded" height={28} width="40%" />
              <Skeleton variant="rounded" height={80} />
            </Stack>
          )}
        </Box>

        <Divider />
        <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ px: { xs: 2.5, sm: 4 }, py: 2 }}>
          <Button onClick={back} disabled={activeStep === 0} startIcon={<BackIcon />} sx={{ color: "text.secondary" }}>
            חזרה
          </Button>
          <Button onClick={next} variant="contained" disabled={activeStep === steps.length - 1} endIcon={<NextIcon />}>
            הבא
          </Button>
        </Stack>
      </Card>
    </Box>
  );
}
